import { useState, type ReactNode } from 'react';
import { QuantityDraftContext } from './quantityDraftContext';

function withMember(
  set: ReadonlySet<string>,
  member: string,
  present: boolean,
): ReadonlySet<string> {
  const next = new Set(set);
  if (present) next.add(member);
  else next.delete(member);
  return next;
}

export function QuantityDraftProvider({ children }: { children: ReactNode }) {
  const [drafts, setDrafts] = useState<ReadonlyMap<string, string>>(new Map());
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());
  const setDraft = (suggestionId: string, value: string | null) => {
    setDrafts((previous) => {
      const next = new Map(previous);
      if (value == null) next.delete(suggestionId);
      else next.set(suggestionId, value);
      return next;
    });
  };
  const trackSave = <T,>(
    suggestionIds: string | readonly string[],
    save: Promise<T>,
  ) => {
    const ids =
      typeof suggestionIds === 'string' ? [suggestionIds] : [...suggestionIds];
    setPending((previous) => {
      let next = previous;
      for (const id of ids) {
        next = withMember(next, id, true);
      }
      return next;
    });
    return save.finally(() =>
      setPending((previous) => {
        let next = previous;
        for (const id of ids) {
          next = withMember(next, id, false);
        }
        return next;
      }),
    );
  };

  return (
    <QuantityDraftContext.Provider
      value={{ drafts, setDraft, pending, trackSave }}
    >
      {children}
    </QuantityDraftContext.Provider>
  );
}
